import { useList, useGo } from "@refinedev/core";
import { useMemo } from "react";
import { Icon } from "@/components/ui/icon";

import { PageHeader } from "@/components/layout/PageHeader";

import type {
  Objective,
  KeyResult,
  Metric,
  Measurement,
  Evidence,
} from "@aos/shared";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/states";
import { StatusBadge } from "@/components/app/StatusBadge";
import { formatDateTime } from "@/lib/format";
import { keyResultProgress, ProgressBar } from "@/resources/key-results/progress";

/** Average a list of 0..100 percentages; returns 0 for an empty list. */
function averagePct(values: number[]): number {
  if (values.length === 0) return 0;
  const sum = values.reduce((acc, v) => acc + v, 0);
  return Math.round(sum / values.length);
}

/**
 * OKRDashboard (D6) — spec §13.3 custom view. Lays out objectives, their key
 * results with black-only progress bars, linked evidence counts, and the
 * metrics catalogue with each metric's latest measurement. All data comes from
 * the generic CRUD list endpoints; aggregation is done client-side.
 */
export function OKRDashboard() {
  const go = useGo();

  const { data: objData, isLoading: objLoading } = useList<Objective>({
    resource: "objectives",
    sorters: [{ field: "created_at", order: "desc" }],
    pagination: { pageSize: 100 },
  });
  const { data: krData, isLoading: krLoading } = useList<KeyResult>({
    resource: "key-results",
    pagination: { pageSize: 500 },
  });
  const { data: metricData, isLoading: metricLoading } = useList<Metric>({
    resource: "metrics",
    pagination: { pageSize: 100 },
  });
  const { data: measurementData } = useList<Measurement>({
    resource: "measurements",
    sorters: [{ field: "measured_at", order: "desc" }],
    pagination: { pageSize: 500 },
  });
  const { data: evidenceData } = useList<Evidence>({
    resource: "evidence",
    pagination: { pageSize: 500 },
  });

  const objectives = useMemo(() => objData?.data ?? [], [objData]);
  const keyResults = useMemo(() => krData?.data ?? [], [krData]);
  const metrics = useMemo(() => metricData?.data ?? [], [metricData]);
  const measurements = useMemo(() => measurementData?.data ?? [], [measurementData]);
  const evidence = useMemo(() => evidenceData?.data ?? [], [evidenceData]);

  /** key results grouped by objective id */
  const krByObjective = useMemo(() => {
    const map = new Map<string, KeyResult[]>();
    for (const kr of keyResults) {
      const list = map.get(kr.objective_id) ?? [];
      list.push(kr);
      map.set(kr.objective_id, list);
    }
    return map;
  }, [keyResults]);

  /** evidence count grouped by artifact id (objective.id is the artifact ref) */
  const evidenceByArtifact = useMemo(() => {
    const map = new Map<string, number>();
    for (const ev of evidence) {
      if (!ev.artifact_id) continue;
      map.set(ev.artifact_id, (map.get(ev.artifact_id) ?? 0) + 1);
    }
    return map;
  }, [evidence]);

  /** latest measurement per metric id (measurements already sorted desc) */
  const latestMeasurementByMetric = useMemo(() => {
    const map = new Map<string, Measurement>();
    for (const m of measurements) {
      if (!map.has(m.metric_id)) map.set(m.metric_id, m);
    }
    return map;
  }, [measurements]);

  const loading = objLoading || krLoading || metricLoading;

  const header = (
    <PageHeader
      title="OKR Dashboard"
      subtitle="Objectives, key results, and the evidence rolling up into outcomes."
      icon="okrs"
      actions={
        <Button
          size="sm"
          onClick={() => go({ to: { resource: "objectives", action: "create" } })}
        >
          New objective
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <div className="space-y-8">
        {header}
        <div className="space-y-6">
          {[0, 1].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-64" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                  <Skeleton className="h-8 w-40" />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {header}

      {objectives.length === 0 ? (
        <EmptyState
          icon="okrs"
          title="No objectives yet"
          hint="Create your first objective to start tracking outcomes."
          action={
            <Button
              size="sm"
              onClick={() =>
                go({ to: { resource: "objectives", action: "create" } })
              }
            >
              New objective
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          {objectives.map((obj) => {
            const krs = krByObjective.get(obj.id) ?? [];
            const objPct = averagePct(krs.map((kr) => keyResultProgress(kr)));
            const evidenceCount = evidenceByArtifact.get(obj.id) ?? 0;
            return (
              <Card key={obj.id}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <button
                      type="button"
                      className="text-left"
                      onClick={() =>
                        go({
                          to: { resource: "objectives", action: "show", id: obj.id },
                        })
                      }
                    >
                      <CardTitle className="hover:underline">{obj.title}</CardTitle>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <StatusBadge status={obj.status} />
                        {obj.period ? (
                          <span className="text-xs text-muted-foreground">
                            {obj.period}
                          </span>
                        ) : null}
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Icon
                            name="evidence"
                            size={14}
                            aria-hidden
                          />{" "}
                          {evidenceCount} evidence
                        </span>
                      </div>
                    </button>
                    <div className="w-40 shrink-0">
                      <div className="mb-1.5 flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Overall</span>
                        <span className="font-semibold tabular-nums">{objPct}%</span>
                      </div>
                      <ProgressBar value={objPct} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {krs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No key results yet.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {krs.map((kr) => {
                        const pct = keyResultProgress(kr);
                        return (
                          <button
                            key={kr.id}
                            type="button"
                            className="block w-full rounded-md p-3 text-left transition-colors hover:bg-accent"
                            onClick={() =>
                              go({
                                to: {
                                  resource: "key-results",
                                  action: "show",
                                  id: kr.id,
                                },
                              })
                            }
                          >
                            <div className="mb-1.5 flex items-center justify-between gap-3">
                              <span className="text-sm font-medium">{kr.title}</span>
                              <span className="text-xs tabular-nums text-muted-foreground">
                                {kr.current_value ?? 0}
                                {kr.unit ? ` ${kr.unit}` : ""} /{" "}
                                {kr.target_value ?? "—"}
                                {kr.unit && kr.target_value != null
                                  ? ` ${kr.unit}`
                                  : ""}{" "}
                                · {pct}%
                              </span>
                            </div>
                            <ProgressBar value={pct} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="space-y-3">
        <h2 className="text-lg font-semibold leading-none tracking-tight">
          Metrics
        </h2>
        {metrics.length === 0 ? (
          <EmptyState
            icon="signals"
            title="No metrics defined"
            hint="Metrics and their measurements appear here once they're recorded."
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {metrics.map((metric) => {
              const latest = latestMeasurementByMetric.get(metric.id);
              return (
                <Card
                  key={metric.id}
                  className="cursor-pointer transition-colors hover:bg-accent"
                  onClick={() =>
                    go({ to: { resource: "metrics", action: "show", id: metric.id } })
                  }
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{metric.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {latest ? (
                      <div className="space-y-1">
                        <div className="text-2xl font-semibold tabular-nums">
                          {latest.value}
                          {metric.unit ? (
                            <span className="ml-1 text-sm font-normal text-muted-foreground">
                              {metric.unit}
                            </span>
                          ) : null}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(latest.measured_at)}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No measurements yet.
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default OKRDashboard;
